import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  IBucketCreateRequest,
  IBucketDetailsItem,
  IBucketListItem,
  IBucketUpdateRequest,
} from "api/entities/bucket";
import { IIdName } from "api/entities/id-name";
import { IShardClusterDetailsItem } from "api/entities/shard-cluster";
import { bucketService } from "api/services/bucket-service";
import { DefaultRouteAction, DefaultRouteParam } from "constants/routes";
import { Formatters } from "../../../../helper/formatters";
import { FormMode, formUtils } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import { IPoolSectionV2Errors } from "../../../common/sections/pool-section/pool-section";
import DetailsPanelFooter from "components/shared-ui/details-panel-footer/details-panel-footer";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import SidePanelSection from "components/shared-ui/side-panel-section/side-panel-section";
import ShardClusterPanel from "../../../shard-clusters/panels/shard-cluster-panel/shard-cluster-panel";
import {
  BucketImmutableSection,
  IBucketImmutableSectionErrors,
} from "../../sections/bucket-immutable-section/bucket-immutable-section";
import {
  BucketNameSection,
  IBucketNameSectionErrors,
} from "../../sections/bucket-name-section/bucket-name-section";
import { BucketUsedBySection } from "../../sections/bucket-used-by-section/bucket-used-by-section";
import { PoolStorageNodesInfoSection } from "../../sections/pool-storage-nodes-info-section/pool-storage-nodes-info-section";
import {
  defaultRetentionSettingV2,
  getRetentionSettings,
  IRetentionSettingsV2,
  IRetentionSettingsV2Errors,
} from "../../sections/retention-section/retention-section-v2";
import styles from "./bucket-panel.module.scss";
import { OwnerInfoSection } from "components/common/sections/details-owner-info/details-owner-info";
import { MetricsInfo } from "components/common/sections/metrics-info/metrics-info";
import { IEntityMetricsResponse } from "api/entities/metrics";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { IAppState } from "redux/reducers";
import { Action } from "redux";
import { tooltipUserGuideActions } from "redux/actions/tooltip-user-guide";
import { jobService } from "api/services/job-service";
import { ShardClusterSection } from "components/buckets/shard-cluster-section";
import { IStoragePoolConfig } from "api/entities/deployment-job";
import { IShardRepositoryDeployRequest } from "api/entities/shard-repository";
import { DeploymentProviders } from "utils/deployment-providers-util";
import {
  BucketV2BlackoutSection,
  IBucketV2BlackoutSectionErrors,
} from "components/buckets/sections/blackout-section/blackout-sections";
import { DateTime } from "luxon";
import { blackoutService } from "api/services/blackout-service";
import { IBucketV2BlackoutEntry } from "api/entities/blackout";

export interface IBucketPanelProps extends ReturnType<
  typeof mapDispatchToProps
> {
  fetchBuckets: () => void;
  mode?: FormMode;
  embedded: boolean;
  onClose: () => void;
}

export interface IBucketFormValue {
  name: string;
  retentionDays?: number;
  objectLock: boolean;
  objectVersioning: boolean;
  storagePoolId: string;
}

interface IBucketErrors
  extends
    IBucketNameSectionErrors,
    IBucketImmutableSectionErrors,
    IRetentionSettingsV2Errors,
    IPoolSectionV2Errors,
    IBucketV2BlackoutSectionErrors {}

const bucketFormDefaultValue: IBucketFormValue = {
  name: "",
  retentionDays: null,
  objectLock: true,
  objectVersioning: true,
  storagePoolId: null,
};

function BucketPanel(props: IBucketPanelProps) {
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();

  const [api, isLoading] = useApi();

  const open = props.embedded ? props.mode === FormMode.Create : !!idOrAction;
  const isBucketCreation = props.embedded
    ? props.mode === FormMode.Create
    : idOrAction === DefaultRouteAction.New;
  const bucketId = !isBucketCreation ? idOrAction : undefined;

  const [bucket, setBucket] = useState<IBucketDetailsItem>(null);
  const [bucketCreateUpdateRequest, setBucketCreateUpdateRequest] =
    useState<IBucketCreateRequest>(bucketFormDefaultValue);
  const [storagePool, setStoragePool] =
    useState<IShardClusterDetailsItem>(null);
  const [selectedStoragePool, setSelectedStoragePool] =
    useState<IIdName<string>>(null);
  const [retention, setRetention] = useState<IRetentionSettingsV2>(
    defaultRetentionSettingV2,
  );
  const [storagePoolConfig, setStoragePoolConfig] =
    useState<IStoragePoolConfig>();

  const [storagePoolPanelFormMode, setShardClusterPanelFormMode] =
    useState<FormMode>(null);
  const [isPoolSectionReadonly, setPoolSectionReadonly] =
    useState<boolean>(false);

  const [errors, setErrors] = useState<IBucketErrors>({});

  const [objectLockSupported, setObjectLockSupported] = useState(true);

  const [metrics, setMetrics] = useState<IEntityMetricsResponse>(null);

  const [recoveryStartTime, setRecoveryStartTime] = useState<
    DateTime | undefined
  >(undefined);

  const [currentRestorePoint, setCurrentRestorePoint] = useState<
    IBucketV2BlackoutEntry | undefined
  >(undefined);

  const [clearCurrentRestorePoint, setClearCurrentRestorePoint] =
    useState<boolean>(false);

  function handleRecoveryStartTimeChange(value: DateTime) {
    setRecoveryStartTime(value);
  }

  function handleClearRecoveryPoint() {
    setClearCurrentRestorePoint(true);
    setCurrentRestorePoint(undefined);
  }

  async function clearRestorePoints() {
    const blackouts = await api(blackoutService.listBucketBlackouts(bucket.id));
    for (const id of Object.keys(blackouts)) {
      await api(blackoutService.deleteBucketBlackout(bucket.id, id));
    }
  }
  async function saveRecoveryPoint(value: DateTime) {
    if (clearCurrentRestorePoint) {
      await clearRestorePoints();
    }

    if (!recoveryStartTime || !bucket) {
      return;
    }

    !clearCurrentRestorePoint && (await clearRestorePoints());

    const request: IBucketV2BlackoutEntry = {
      Begin: recoveryStartTime.toUTC().toISO(),
      End: DateTime.utc().toISO(),
    };

    return api(blackoutService.setBucketBlackout(bucket.id, request));
  }

  async function fetchRestorePoint(bucketId: string) {
    return api(blackoutService.listBucketBlackouts(bucketId)).then(
      (blackouts) => {
        if (Object.keys(blackouts).length > 0) {
          const [[_, firstEntry]] = Object.entries(blackouts);
          return firstEntry;
        }
        return undefined;
      },
    );
  }

  function canProceed() {
    return (
      !Object.keys(errors).some((key) => errors[key]) &&
      (!!selectedStoragePool || !!storagePoolConfig) &&
      !!bucketCreateUpdateRequest.name
    );
  }

  function handleError(changes: Partial<IBucketErrors>) {
    setErrors((errors) => ({ ...errors, ...changes }));
  }

  function handleChange(changes: Partial<IBucketCreateRequest>) {
    const updatedRequest = { ...bucketCreateUpdateRequest, ...changes };
    setBucketCreateUpdateRequest(updatedRequest);
  }

  function handleStoragePoolChange(
    changes: IIdName<string> | IStoragePoolConfig,
  ) {
    if ("contentStorages" in changes) {
      setStoragePoolConfig(changes);
    } else {
      handleChange({ storagePoolId: changes.id });
      setSelectedStoragePool(changes);
    }
  }

  function close() {
    setSelectedStoragePool(null);
    setRecoveryStartTime(undefined);
    props.onClose();
  }

  function save() {
    const bucketRequest = formUtils.trimStringValues(bucketCreateUpdateRequest);
    if (isBucketCreation) {
      return handleCreateBucket(bucketRequest);
    } else {
      return handleUpdateBucket({ id: bucketId, ...bucketRequest });
    }
  }

  function handleCreateBucket(
    createRequest: IBucketCreateRequest,
  ): Promise<void> {
    // return api(bucketService.create(createRequest)).then((bucket) => {
    //   api(clusterService.getByStoragePoolId(bucket.storagePoolId)).then(
    //     (cluster) => {
    //       api(jobService.createLkeBzs(cluster.id, bucket.name)).then((res) => {
    //         props.fetchBuckets();
    //         if (accountService.getTooltipUserGuideState().showTooltip) {
    //           props.bucketTooltipOpen();
    //         }
    //         close();
    //       });
    //     }
    //   );

    //   return bucket;
    // });

    return api(
      jobService
        .createBzs({
          name: createRequest.name,
          storagePoolConfig: storagePoolConfig,
          enableObjectLock: createRequest.objectLock,
        })
        .then((id) => {
          props.fetchBuckets();
          close();
        }),
    );
  }

  function handleUpdateBucket(
    updateRequest: IBucketUpdateRequest,
  ): Promise<IBucketListItem> {
    return api(bucketService.update(bucketId, updateRequest)).then((bucket) => {
      return saveRecoveryPoint(recoveryStartTime).then(() => {
        props.fetchBuckets();
        close();
        return bucket;
      });
    });
  }

  function handleAddShardClusterFormPanelOpen() {
    setPoolSectionReadonly(true);
    setShardClusterPanelFormMode(FormMode.Create);
  }
  function handleAddShardClusterFormPanelClose() {
    setPoolSectionReadonly(false);
    setShardClusterPanelFormMode(null);
  }

  useEffect(() => {
    // TODO validate if object lock is supported by providers
    if (!storagePoolConfig) return;

    let objectLockSupported = true;

    for (const node of [
      ...storagePoolConfig.contentStorages,
      ...storagePoolConfig.metaStorages,
      ...storagePoolConfig.keyStorages,
    ]) {
      if (!("provider" in node)) continue;

      if (
        node.provider !== DeploymentProviders.Wasabi &&
        node.provider !== DeploymentProviders.Aws
      ) {
        objectLockSupported = false;
        break;
      }
    }

    setObjectLockSupported(objectLockSupported);
  }, [storagePoolConfig]);

  useEffect(() => {
    if (open) {
      if (isBucketCreation) {
        setBucketCreateUpdateRequest(bucketFormDefaultValue);
        setBucket(null);
        setStoragePool(null);
        setSelectedStoragePool(null);
        setRetention(defaultRetentionSettingV2);
      } else {
        api(bucketService.get(bucketId)).then((bucket) => {
          setBucketCreateUpdateRequest({
            name: bucket.name,
            retentionDays: bucket.bucketConfig?.retentionDays,
            objectLock: bucket.bucketConfig?.objectLock,
            objectVersioning: bucket.bucketConfig?.objectVersioning,
            storagePoolId: bucket.storagePoolId,
          });

          setBucket(bucket);
          setStoragePool(bucket.storagePool);
          setSelectedStoragePool(bucket.storagePool);
          setRetention(getRetentionSettings(bucket.bucketConfig));
        });

        // api(bucketService.getMetrics(bucketId)).then((metrics) => {
        //   if (!!metrics && metrics.length > 0) {
        //     setMetrics(metrics[0]);
        //   }
        // });
        fetchRestorePoint(bucketId).then((restorePoint) => {
          setCurrentRestorePoint(restorePoint);
        });
      }

      setErrors({});
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <>
      <DetailsPanel
        open={open}
        isLoading={isLoading}
        onCloseClick={close}
        title={isBucketCreation ? "Add New Bucket" : "Bucket Details"}
        className={styles.panel}
      >
        {open && (
          <div className={styles["bucket-info"]}>
            {!!bucket && (
              <>
                <section className={styles["bucket-info-block"]}>
                  <section className={styles["created-uri"]}>
                    <div>Id: {bucket.id}</div>

                    <OwnerInfoSection entity={bucket} />

                    <div>
                      Created At: {Formatters.formatDateTime(bucket.createdAt)}
                    </div>
                    <div>Resource URI: {bucket.tagURI}</div>
                    {bucket.bucketConfig?.objectLock && (
                      <>
                        <div>Object Lock: Enabled</div>
                        <div>Object Versioning: Enabled</div>
                      </>
                    )}
                  </section>
                </section>

                <MetricsInfo
                  entityName="Bucket"
                  metrics={metrics}
                  className={styles.metrics}
                />
              </>
            )}

            {isBucketCreation && (
              <div className={styles.description}>
                A Bucket is a storage container used to organize and manage data
                from user applications. It offers flexibility and scalability
                while ensuring easy access to your stored data. Buckets serve as
                central locations for data management, allowing seamless
                integration with various applications.
              </div>
            )}

            <BucketNameSection
              className={styles.name}
              bucketCreateUpdateRequest={bucketCreateUpdateRequest}
              onChange={handleChange}
              onError={handleError}
              errors={errors}
              readOnly={!isBucketCreation}
            />

            {isBucketCreation && (
              <BucketImmutableSection
                className={styles.immutable}
                bucketCreateUpdateRequest={bucketCreateUpdateRequest}
                disabled={!isBucketCreation || !objectLockSupported}
                onChange={handleChange}
                onError={handleError}
                errors={errors}
              />
            )}

            {/* <RetentionSectionV2
              retentionSettings={retention}
              errors={errors}
              onError={handleError}
              onChange={setRetention}
            /> */}

            {isBucketCreation ? (
              <ShardClusterSection
                bucketName={bucketCreateUpdateRequest.name}
                onChange={handleStoragePoolChange}
                onNewStoragePoolClick={handleAddShardClusterFormPanelOpen}
                storagePool={selectedStoragePool}
                isReadonly={isPoolSectionReadonly}
                errors={errors}
                onError={handleError}
              />
            ) : (
              // <PoolSection
              //     onChange={handleStoragePoolChange}
              //     onNewStoragePoolClick={handleAddShardClusterFormPanelOpen}
              //     storagePool={selectedStoragePool}
              //     errors={errors}
              //     isReadonly={isPoolSectionReadonly}
              //     onError={handleError}
              // />

              !!storagePool && (
                <SidePanelSection
                  title={`Shard Cluster: ${storagePool?.name}`}
                  testId="storage-pool-info"
                >
                  <PoolStorageNodesInfoSection storagePool={storagePool} />
                </SidePanelSection>
              )
            )}

            <BucketUsedBySection bucketId={bucket?.id} />

            {!isBucketCreation && (
              <BucketV2BlackoutSection
                onClear={handleClearRecoveryPoint}
                onChange={handleRecoveryStartTimeChange}
                activeRestorePoint={currentRestorePoint}
                onError={handleError}
              />
            )}

            <DetailsPanelFooter
              sticky={true}
              saveButtonText={"Save"}
              saveButtonDisabled={!canProceed()}
              onSaveButtonClick={save}
              onCancelButtonClick={close}
            />
          </div>
        )}
      </DetailsPanel>

      <ShardClusterPanel
        embedded={true}
        mode={storagePoolPanelFormMode}
        onClose={handleAddShardClusterFormPanelClose}
      />
    </>
  );
}

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
  return {
    bucketTooltipOpen: () => {
      dispatch(tooltipUserGuideActions.disableStepCounter());
      dispatch(tooltipUserGuideActions.open());
      dispatch(tooltipUserGuideActions.setStep("accessCredentials"));
    },
  };
}

export default connect(null, mapDispatchToProps)(BucketPanel);
